import React, { useEffect, useState } from 'react';
import { IoOpenOutline } from 'react-icons/io5';

import {
  Notice,
  Section,
  badge,
  errorText,
  formatDate,
  mono,
} from './PipelineSettingsPanel';
import { configApi } from '../../services/api';

// Configuration → Health. How the AI features are doing right now: model
// calls per use (failures, p95 latency), usage and recorded cost per feature,
// and the last run of every scheduled job. Read-only.

const WINDOWS = [
  [24, '24 hours'],
  [24 * 7, '7 days'],
  [24 * 30, '30 days'],
];

// A job whose next run is this far in the past has stopped being scheduled.
const LATE_AFTER_MS = 15 * 60 * 1000;

const th = 'px-3 py-2 text-left text-xs font-medium text-zinc-400';
const td = 'px-3 py-2 text-sm tabular-nums text-zinc-200';
const num = `${td} text-right`;

const percent = (part, whole) =>
  whole
    ? `${((100 * part) / whole).toFixed(part && part < whole / 100 ? 1 : 0)}%`
    : '—';

const seconds = (row) => {
  if (row.p95_over_max) return '> 120 s';
  if (row.p95_seconds == null) return '—';
  return `≤ ${row.p95_seconds} s`;
};

const jobState = (job, now) => {
  if (job.last_status === 'failed')
    return ['failed', 'bg-red-500/15 text-red-300'];
  if (job.last_status === 'missed')
    return ['missed', 'bg-amber-500/10 text-amber-300'];
  if (job.next_run_at && now - new Date(job.next_run_at) > LATE_AFTER_MS)
    return ['not running', 'bg-amber-500/10 text-amber-300'];
  if (job.last_status === 'ok')
    return ['ok', 'bg-emerald-500/10 text-emerald-300'];
  return ['running', 'bg-sky-500/10 text-sky-300'];
};

const Table = ({ head, children }) => (
  <div className="overflow-x-auto rounded-md border border-zinc-800">
    <table className="w-full min-w-[560px]">
      <thead className="bg-zinc-900/60">
        <tr>
          {head.map(([label, right]) => (
            <th key={label} className={`${th} ${right ? 'text-right' : ''}`}>
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-800">{children}</tbody>
    </table>
  </div>
);

export default function HealthPanel({ token }) {
  const [hours, setHours] = useState(24);
  const [state, setState] = useState({ data: null, error: '' });

  useEffect(() => {
    let stale = false;
    setState((s) => ({ ...s, error: '' }));
    configApi
      .health(token, hours)
      .then((data) => !stale && setState({ data, error: '' }))
      .catch((e) => !stale && setState({ data: null, error: errorText(e) }));
    return () => {
      stale = true;
    };
  }, [token, hours]);

  const { data, error } = state;
  const now = Date.now();
  const totalCost = (data?.features || []).reduce((sum, f) => sum + f.cost, 0);
  const safeGrafana = /^https?:\/\//.test(data?.grafana_url || '')
    ? data.grafana_url
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          role="group"
          aria-label="Time window"
          className="flex gap-1 rounded-lg bg-zinc-900 p-1"
        >
          {WINDOWS.map(([value, label]) => (
            <button
              key={value}
              aria-pressed={hours === value}
              onClick={() => setHours(value)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 ${
                hours === value
                  ? 'bg-zinc-700 text-white'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {safeGrafana && (
          <a
            href={safeGrafana}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-indigo-300 hover:text-indigo-200"
          >
            Pipeline metrics in Grafana <IoOpenOutline aria-hidden />
          </a>
        )}
      </div>

      {error && <Notice>{error}</Notice>}
      {!data && !error && <p className="text-sm text-zinc-400">Loading…</p>}

      {data && (
        <>
          <Section
            title="Model calls"
            hint="Every call through the AI gateway, by the use it was made for. Latency is the bucket the 95th percentile falls in."
          >
            {data.models.length === 0 ? (
              <p className="text-sm text-zinc-400">
                No calls recorded in this window. Calls are counted from the
                first deploy of this version.
              </p>
            ) : (
              <Table
                head={[
                  ['Use'],
                  ['Calls', true],
                  ['Failed', true],
                  ['p95', true],
                  ['Last error'],
                ]}
              >
                {data.models.map((m) => (
                  <tr key={m.use}>
                    <td className={`${td} ${mono}`}>{m.use}</td>
                    <td className={num}>{m.calls.toLocaleString()}</td>
                    <td
                      className={`${num} ${m.failures ? 'text-red-300' : ''}`}
                    >
                      {m.failures
                        ? `${m.failures} · ${percent(m.failures, m.calls)}`
                        : '0'}
                    </td>
                    <td className={num}>{seconds(m)}</td>
                    <td className="px-3 py-2 text-xs text-zinc-400">
                      {m.last_error
                        ? `${m.last_error.type} · ${formatDate(m.last_error.at)}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Section>

          <Section
            title="Usage by feature"
            hint="Calls that recorded tokens and cost, with the cost markup applied."
          >
            {data.features.length === 0 ? (
              <p className="text-sm text-zinc-400">
                No recorded usage in this window.
              </p>
            ) : (
              <Table
                head={[
                  ['Feature'],
                  ['Calls', true],
                  ['Tokens', true],
                  ['Cost (USD)', true],
                ]}
              >
                {data.features.map((f) => (
                  <tr key={f.feature}>
                    <td className={`${td} ${mono}`}>{f.feature}</td>
                    <td className={num}>{f.calls.toLocaleString()}</td>
                    <td className={num}>{f.tokens.toLocaleString()}</td>
                    <td className={num}>{f.cost.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="bg-zinc-900/40">
                  <td className={`${td} font-medium`}>Total</td>
                  <td className={num} />
                  <td className={num} />
                  <td className={`${num} font-medium`}>
                    {totalCost.toFixed(2)}
                  </td>
                </tr>
              </Table>
            )}
          </Section>

          <Section
            title="Scheduled jobs"
            hint="Last run of each background job, in the API and the token-refresh worker."
          >
            {data.jobs.length === 0 ? (
              <p className="text-sm text-zinc-400">
                No job has run since this version was deployed.
              </p>
            ) : (
              <ul className="divide-y divide-zinc-800">
                {data.jobs.map((job) => {
                  const [label, tone] = jobState(job, now);
                  return (
                    <li
                      key={job._id}
                      className="flex flex-wrap items-start justify-between gap-2 py-2"
                    >
                      <div className="min-w-0">
                        <span className={`${mono} text-zinc-200`}>
                          {job.name}
                        </span>{' '}
                        <span className="text-xs text-zinc-400">
                          {job.process} · {job.schedule}
                        </span>
                        {job.last_status === 'failed' && job.last_error && (
                          <div className="text-xs text-red-300">
                            {job.last_error}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs tabular-nums text-zinc-400">
                        <span>
                          last{' '}
                          {formatDate(job.last_end_at || job.last_start_at) ||
                            '—'}
                          {job.next_run_at
                            ? ` · next ${formatDate(job.next_run_at)}`
                            : ''}
                          {job.failures ? ` · ${job.failures} failed` : ''}
                        </span>
                        <span className={`${badge} ${tone}`}>{label}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
